import { Box, Text, VStack, HStack, Button, Select } from "@chakra-ui/react";
import { FaEdit, FaCheck } from "react-icons/fa";
import { useState } from "react";
import { useFormContext } from "react-hook-form";
import { AdSetFormData } from "../../forms/adset";
import EditLocationsWidget from "../EditLocationsWidget";
import { StepProps } from "../../types";

const GENDER_OPTIONS = [
  { key: "all", value: "all" },
  { key: "male", value: "male" },
  { key: "female", value: "female" },
];

const AdvantagePlusContent = ({
  search,
  loading = {},
}: {
  search?: StepProps["search"];
  loading?: Record<string, boolean>;
}) => {
  const { register, watch, setValue } = useFormContext<AdSetFormData>();
  const [isEditingLocations, setIsEditingLocations] = useState(false);

  const targetingOptions = [
    ...(watch("geo_markets") || []).map((g) => ({
      type: "geo_markets",
      value: g.key,
      name: g.name || g.value,
    })),
    ...(watch("countries") || []).map((g) => ({
      type: "countries",
      value: g.key,
      name: g.value,
    })),
  ];

  return (
    <VStack
      w="full"
      spacing={4}
      align="stretch"
      p={4}
      borderRadius="xl"
      border="1px solid"
      borderColor="gray.200"
      bg="white"
      position="relative"
      zIndex={100}
      _before={{
        content: '""',
        position: "absolute",
        top: 0,
        left: 0,
        height: "100%",
        right: 0,
        zIndex: 0,
        background:
          "radial-gradient(ellipse at center top, rgba(255, 255, 229, 0.8) 0%, rgba(193, 225, 252, 0.3) 40%, transparent 60%)",
        borderRadius: "xl xl 0 0",
        pointerEvents: "none",
      }}
    >
      {/* Header */}
      <VStack
        w="full"
        spacing={2}
        align="stretch"
        p={4}
        borderRadius="lg"
        position="relative"
        zIndex={1}
      >
        <Text fontSize="lg" fontWeight="semibold" color="gray.800">
          Meta Advantage+ audience
        </Text>
        <Text fontSize="sm" color="gray.600" lineHeight="1.5">
          Meta's advanced AI will find your audience. Advantage+ audiences
          typically get better results compared with manual audience creation.
        </Text>
      </VStack>

      {/* Form Sections */}
      <VStack
        w="full"
        spacing={4}
        align="stretch"
        position="relative"
        zIndex={1}
      >
        {/* Locations Section */}
        <Box
          p={4}
          rounded="xl"
          border="1px solid"
          borderColor="gray.200"
          bg="gray.50"
        >
          <HStack w="full" justifyContent="space-between" alignItems="center">
            {isEditingLocations ? (
              <EditLocationsWidget isLoading={loading.search} search={search} />
            ) : (
              <>
                <VStack align="flex-start" spacing={0}>
                  <Text fontWeight="bold" fontSize="md" color="gray.800">
                    Locations
                  </Text>
                  {targetingOptions.length > 0 && (
                    <Text fontSize="sm" color="gray.600">
                      Inclusion:{" "}
                      {targetingOptions
                        .map((t) => t.name || t.value)
                        .join(", ")}
                    </Text>
                  )}
                </VStack>
              </>
            )}
            <Button
              size="sm"
              variant="ghost"
              leftIcon={
                isEditingLocations ? (
                  <FaCheck size={12} />
                ) : (
                  <FaEdit size={12} />
                )
              }
              colorScheme="gray"
              onClick={() => setIsEditingLocations(!isEditingLocations)}
            >
              {isEditingLocations ? "Save" : "Edit"}
            </Button>
          </HStack>
        </Box>

        <HStack w="full" spacing={4}>
          {/* Age Section */}
          <Box
            p={4}
            rounded="xl"
            border="1px solid"
            borderColor="gray.200"
            bg="gray.50"
            flexGrow={1}
          >
            <Text fontWeight="bold" fontSize="md" color="gray.800" mb={2}>
              Age
            </Text>
            <HStack w="full" spacing={2}>
              <Select
                value={watch("age_min")}
                onChange={(e) => setValue("age_min", Number(e.target.value))}
                size="md"
                bg="white"
                borderRadius="md"
              >
                {Array.from({ length: 83 }, (_, i) => i + 13).map((age) => (
                  <option key={age} value={age.toString()}>
                    {age}
                  </option>
                ))}
              </Select>
              <Text fontSize="sm" color="gray.500">
                -
              </Text>
              <Select
                value={watch("age_max")}
                onChange={(e) => setValue("age_max", Number(e.target.value))}
                size="md"
                bg="white"
                disabled
                borderRadius="md"
              >
                <option key="65" value="65">
                  65
                </option>
              </Select>
              {/* <Box
                  p={2}
                  bg="gray.100"
                  borderRadius="md"
                  fontSize="xs"
                  backgroundColor="yellow.50"
                >
                  <Text>Audience+ audience max age is 65</Text>
                </Box> */}
            </HStack>
          </Box>

          {/* Gender Section */}
          <Box
            p={4}
            rounded="xl"
            border="1px solid"
            borderColor="gray.200"
            bg="gray.50"
            flexGrow={1}
          >
            <Text fontWeight="bold" fontSize="md" color="gray.800" mb={2}>
              Gender
            </Text>
            <Select
              value={watch("genders") || "all"}
              onChange={(e) => setValue("genders", e.target.value)}
              size="md"
              bg="white"
              borderRadius="md"
            >
              {GENDER_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.value}
                </option>
              ))}
            </Select>
          </Box>
        </HStack>
      </VStack>
    </VStack>
  );
};

export default AdvantagePlusContent;
